// v2 wall — a dressed ashlar curtain wall in warm sandstone: every junction is a
// pilaster (a dark plinth, a dressed shaft and a projecting cap), every run a
// coursed wall body under a pale projecting coping that carries a battlemented row
// of merlons once the wall is finished. Ported 1:1 from age-of-mcp's v2/wall.ts,
// using the library's shared `roadwork` (armFrame, armPoint) helpers. The per-tile
// network params (connections / heights) and the gate conversion are not modelled
// here, so this renders the default lone-junction look the flattened file bakes
// (connections = 0, not a gate -> just the pilaster rising through the build
// stages). The gate portal is omitted as it is never the default render.
import * as THREE from "three";
import { defineModel, roadwork, type Part } from "../../lib";

// Warm dressed-sandstone palette.
const PLINTH = "#776c59";   // dark footing course
const SHAFT  = "#a89a7f";   // pilaster shaft / drum-tower ashlar
const BODY   = "#9a8d73";   // coursed wall body
const COPING = "#c3b79c";   // pale projecting coping, caps and merlons

// Rampart height (top of the coping) per build stage — it rises as it builds.
const STAGE_H = [0.12, 0.22, 0.36, 0.5];
const THICK = 0.15;   // thickness of a wall-body segment
const COP_H = 0.07;   // coping course height (the top of the run)

// The junction pilaster: plinth, dressed shaft, projecting cap.
function pilaster(h: number): Part[] {
  const shaftH = h + 0.02;
  return [
    { geometry: new THREE.BoxGeometry(0.30, 0.06, 0.30),
      color: PLINTH, position: [0, 0.03, 0] },
    { geometry: new THREE.BoxGeometry(0.21, shaftH, 0.21),
      color: SHAFT, position: [0, shaftH / 2, 0] },
    { geometry: new THREE.BoxGeometry(0.27, 0.055, 0.27),
      color: COPING, position: [0, shaftH + 0.0275, 0] },
  ];
}

// Half-arms reaching out to every neighbouring wall tile. With no connections
// this contributes nothing; kept verbatim so the wall's structure mirrors the
// source whenever a network supplies its mask.
function connectionArms(
  h: number, s: number, connections: number, heights: readonly number[],
): Part[] {
  const has = (i: number): boolean => (connections & (1 << i)) !== 0;
  const parts: Part[] = [];
  const bodyH = Math.max(0.06, h - COP_H);
  for (let i = 0; i < 8; i++) {
    if (!has(i)) continue;
    const diagonal = (i & 1) === 1;
    if (diagonal && (has((i + 7) % 8) || has((i + 1) % 8))) continue;
    const f = roadwork.armFrame(i, heights);
    const rot: [number, number, number] = [0, f.yaw, f.pitch];
    // Coursed wall body — the half-arm's midpoint, halfway up the rise.
    parts.push({
      geometry: new THREE.BoxGeometry(f.len3D, bodyH, THICK),
      color: BODY,
      position: [f.dx * 0.25, bodyH / 2 + f.dY * 0.5, f.dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });
    // Pale projecting coping course riding the body top.
    parts.push({
      geometry: new THREE.BoxGeometry(f.len3D + 0.02, COP_H, THICK + 0.07),
      color: COPING,
      position: [f.dx * 0.25, bodyH + COP_H / 2 + f.dY * 0.5, f.dz * 0.25],
      rotation: rot, eulerOrder: "YZX",
    });
    // Battlements: merlon blocks stud the finished coping, following the slope.
    if (s >= 3) {
      for (const t of [0.34, 0.7]) {
        parts.push({
          geometry: new THREE.BoxGeometry(0.11, 0.075, THICK + 0.03),
          color: COPING,
          position: roadwork.armPoint(f, t, h + 0.0375),
          rotation: rot, eulerOrder: "YZX",
        });
      }
    }
  }
  return parts;
}

function stage(s: number, connections = 0, heights: readonly number[] = []): Part[] {
  const h = STAGE_H[Math.max(0, Math.min(3, s))]!;
  return [
    ...pilaster(h),
    ...connectionArms(h, s, connections, heights),
  ];
}

export const wallV2 = defineModel({
  id: "wall-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Wall (v2)",
  tags: ["building","wall","low-poly","age-of-mcp","fable","military"],
  summary: "Low-poly wall.",
  description: "A stone rampart segment, placed in runs to form a defensive wall.",
  author: "age-of-mcp",
  footprint: 0.32,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
